import { SchemaPath, validate } from '@angular/forms/signals';

export interface PasswordPair {
  password: string;
  verifyPassword: string;
}

/** Shortest password accepted when one is set. Must match saas-service's MIN_PASSWORD_LENGTH. */
export const MIN_PASSWORD_LENGTH = 12;

/** bcrypt ignores everything after this many bytes. Must match saas-service's MAX_PASSWORD_BYTES. */
export const MAX_PASSWORD_BYTES = 72;

export const PASSWORD_RULE_MESSAGE = `Password must be ${MIN_PASSWORD_LENGTH} to ${MAX_PASSWORD_BYTES} characters long and include uppercase, lowercase, number, and symbol.`;

export const isStrongPassword = (value: string): boolean =>
  value.length >= MIN_PASSWORD_LENGTH &&
  new TextEncoder().encode(value).length <= MAX_PASSWORD_BYTES &&
  /[a-z]/.test(value) &&
  /[A-Z]/.test(value) &&
  /\d/.test(value) &&
  /[^A-Za-z0-9]/.test(value);

/** Adds a `weakPassword` error. An empty value is left to required(), so it is not reported twice. */
export function strongPassword(path: SchemaPath<string>): void {
  validate(path, ({ value }) =>
    !value() || isStrongPassword(value()) ? undefined : { kind: 'weakPassword' },
  );
}

/**
 * Adds a `passwordMismatch` error on the form ROOT, not on a field: it blocks submit and disables
 * the button, and the template shows its own message, instead of putting a tip on the confirm field.
 */
export function passwordsMatch<T extends PasswordPair>(form: SchemaPath<T>): void {
  validate(form, ({ value }) => {
    const { password, verifyPassword } = value();

    return password && verifyPassword && password !== verifyPassword
      ? { kind: 'passwordMismatch' }
      : undefined;
  });
}
